import React from 'react'
import { Input, FormGroup, FormControlLabel, useTheme } from '@mui/material'
import { useHistory } from 'react-router'
import { SecurityUser, Sms, User } from 'iconsax-react'

import RolesMenu from '../../../molecules/RolesMenu'
import Switch from '../../../atoms/Switch'
import ErrorToast from '../../../molecules/ErrorToast'
import Button from '../../../atoms/Button2'

import { useEditContext } from '@SonarCX/providers/EditProvider'
import { useCustomerContext } from '@SonarCX/providers/CustomerProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import useObjectChanged from '@SonarCX/hooks/useObjectChanged'
import useStyles from './styles'

interface UserTabProps {
  details: LocalUser
  update: () => void
  updateUser: (user: any) => void
  setDetails: (details: any) => void
  canEdit: boolean
}
const UserTab: React.FC<UserTabProps> = ({
  details,
  update,
  updateUser,
  setDetails,
  canEdit,
  ...props
}) => {
  const history = useHistory()
  const theme = useTheme()

  const { setAuth, setEditedAuthObj, authSet } = useObjectChanged()
  const { setEditedAuth } = useEditContext()
  const { handleUpdateUser } = useCustomerContext()
  const { down770 } = useBreakpoint()

  const [values, setValues] = React.useState({
    name: '',
    email: '',
    suspended_at: ''
  })
  const [checked, setChecked] = React.useState(false)
  const [error, setError] = React.useState(null)

  const classes = useStyles({ ...props, smallMedia: down770 })

  const handleUploadReturn = async () => {
    try {
      const success = await handleUpdateUser()

      if (success) {
        const location = history.location.pathname.split('/')
        const newLocation = location.slice(0, location.length - 1).join('/')

        // Gives a chance for the save notification to show
        setTimeout(() => history.push(newLocation), 500)
      }
    } catch (err) {
      setError({ err })
    }
  }

  React.useEffect(() => {
    if (details) {
      setChecked(!Boolean(details.auth?.suspended_at))

      if (!authSet) {
        if (details.auth) {
          setAuth({ details: details.auth, exists: true })
        } else {
          setAuth({ details: {}, exists: true })
        }
      }

      setValues({
        ...details,
        name: details.name,
        suspended_at: details.auth?.suspended_at ?? null,
        email: details.auth?.email || ''
      })
    }
  }, [details, setAuth, authSet])

  const handleChange = prop => e => {
    const val = e.target.value.trim()
    setValues({ ...values, [prop]: val })
    setDetails(details => ({
      ...details,
      auth: { ...details.auth, email: val.trim().replaceAll(' ', '') }
    }))

    setEditedAuthObj({ ...details.auth, email: val })
  }

  const toggleChecked = e => {
    const checked = e.target.checked

    setChecked(checked)
    const suspended_at = checked ? null : new Date().toISOString()

    setValues(old => ({ ...old, suspended_at }))

    setDetails({
      ...values,
      auth: {
        //@ts-ignore
        ...values.auth,
        suspended_at
      }
    })

    setEditedAuthObj({
      ...details.auth,
      suspended_at: checked
    })

    setAuth({
      ...details.auth,
      suspended_at
    })
  }

  return (
    <div className={classes.root}>
      {down770 && (
        <div className={classes.title}>
          <User
            variant='Bold'
            color='grey'
            size={theme.size.base}
            style={{ marginRight: '9px' }}
          />
          USER ACCOUNT
        </div>
      )}

      <form className={classes.form}>
        <div>
          <div className={classes.label}>
            <Sms
              variant='Bold'
              color='grey'
              size={theme.size.base}
              className={classes.icon}
            />
            Email
          </div>

          <Input
            type='email'
            placeholder='Email'
            value={values.email.trim()}
            onChange={handleChange('email')}
            className={classes.textField}
            disableUnderline={true}
            disabled={!canEdit}
            inputProps={{
              style: { textOverflow: 'ellipsis' }
            }}
          />
        </div>

        {details.auth && (
          <>
            <div>
              <div className={classes.label}>
                <SecurityUser
                  variant='Bold'
                  color='grey'
                  size={theme.size.base}
                  className={classes.icon}
                />
                Role
              </div>

              <RolesMenu
                selected={details.role_id}
                onSelect={id => {
                  setDetails(old => ({ ...old, role_id: id }))
                  setEditedAuth(true)
                }}
              />
            </div>

            <FormGroup>
              <FormControlLabel
                className={classes.switch}
                label='Active'
                control={<Switch checked={checked} onChange={toggleChecked} />}
              />
            </FormGroup>
          </>
        )}
      </form>
      {down770 && (
        <div className={classes.buttonContainer}>
          <Button
            style={{ width: '60%', margin: '1rem auto 0.5rem auto' }}
            onClick={() => handleUploadReturn()}
          >
            SAVE &amp; CLOSE
          </Button>
        </div>
      )}
      <ErrorToast error={error} />
    </div>
  )
}

export default UserTab
